﻿/*
 * Interfaces and functions used for fetching pick report object
 */

import { IPickReport } from "./IPickReports";
import GeoJsonSpaceProperties from "../GeoJsonSpaceProperties";

/** Includes data required for getting pickreports. Data is gathered from a map click event. */
export interface IPickReportQuery {
    lon: number;
    lat: number;
    zoom: number;
}

/** Includes additional data required for getting pickreports. Data not related to map click event. */
export interface IPickReportQueryAdditional {
    /** User token. */
    token: string;
    /** Layer detail level (low, full, etc). */
    layer: string;
}

export interface IPickReportSpaceFeature {
    id?: number,
    geometry?: GeoJSON.GeoJSON,
    properties?: Partial<GeoJsonSpaceProperties>
}

/** IPickReportQuery with optional client-side gathered data. */
export interface IPickReportQueryExtended extends IPickReportQuery {    
    depth?: number,
    spaceFeatures?: IPickReportSpaceFeature[]
}

/** Dictionary for replacing substrings in url with parameters. */
export type PickReportUrlReplace = { [key in keyof (IPickReportQuery & IPickReportQueryAdditional)]: string };
export const DEFAULT_PICK_REPORT_REPLACE: PickReportUrlReplace = {
    lat: "{lat}",
    lon: "{lon}",
    zoom: "{zoom}",
    layer: "{layer}",
    token: "{token}"
}

/** Async function for fetching pick report objects. */
export type FetchPickReportsFunction = (request: IPickReportQuery, additional: IPickReportQueryAdditional) => Promise<IPickReport[] | undefined>;

/** Interface for fetching pick report objects. */
export interface IFetchPickReports {  
    getReports: FetchPickReportsFunction;
}

export type PickReportUrl = string | FetchPickReportsFunction | IFetchPickReports;

/** Default implementation for fetching pick report objects. */
export class FetchPickReports implements IFetchPickReports {
    
    constructor(url: string);
    
    constructor(url: string, urlReplace?: Partial<PickReportUrlReplace>);
    constructor(
        public url: string,
        public urlReplace: Partial<PickReportUrlReplace> = DEFAULT_PICK_REPORT_REPLACE) {
    }

    public async getReports(request: IPickReportQuery, params: IPickReportQueryAdditional) {
        
        const { lat, lon, zoom } = request;
        const { token, layer } = params;

        const urlReplace = {
            ...this.urlReplace,
            ...DEFAULT_PICK_REPORT_REPLACE
        }

        const url = this.url.slice()
            .replace(urlReplace.lat, `${lat}`)
            .replace(urlReplace.lon, `${lon}`)
            .replace(urlReplace.zoom, `${zoom}`)
            .replace(urlReplace.layer, `${layer}`)
            .replace(urlReplace.token, `${token}`);

        const response = await fetch(url);
        return await response.json() as (IPickReport[] | undefined);
    }
}